/**
 * @file src/room/wire.js
 * @description Pure wire helpers for the room event layer: OSC-style addresses, the stamped
 * envelope every outgoing player event carries, and per-sender sequence tracking with gap
 * detection. No I/O, no singletons — the room client composes these.
 *
 * Wire voice ids are POSITIONAL ("0"…"N-1", roster order) — the relay knows the room's shape,
 * not our runtime voice keys. Translation between runtime voice ids and wire ids happens at the
 * room-store boundary; everything in this file speaks wire ids.
 */

/**
 * The grant map carried by a room message — wire voice id → holder clientId → grant detail — or
 * null when the message states nothing about grants (which is different from stating that nobody
 * holds anything).
 *
 * @param {{ grants?: unknown }} msg
 * @returns {Record<string, Record<string, object>>|null}
 */
export function grantsFromWire(msg) {
  if (!msg?.grants || typeof msg.grants !== 'object') return null;
  const grants = {};
  for (const [wireId, holders] of Object.entries(msg.grants)) {
    if (!holders || typeof holders !== 'object') continue;
    const entry = holderEntry(Object.keys(holders));
    // A voice nobody holds is ABSENT, never present-and-empty: every reader then asks one question
    // ("is this client a holder?") instead of two.
    if (Object.keys(entry).length > 0) grants[String(wireId)] = entry;
  }
  return grants;
}

/**
 * A holder set built on a NULL prototype.
 *
 * Assigning `entry['__proto__'] = {}` on an ordinary object runs the inherited `__proto__` setter
 * and creates no own property, while the relay builds its holders with a computed literal, which
 * does. A client id of `__proto__` would therefore be a real holder on the relay — free to write —
 * and no holder at all here, so the two ends would disagree about who is driving. A null prototype
 * removes the setter, and the own-property tests downstream then answer the same as the relay's.
 */
function holderEntry(clientIds) {
  const entry = Object.create(null);
  for (const clientId of clientIds) {
    if (typeof clientId === 'string' && clientId) entry[clientId] = {};
  }
  return entry;
}

/** Every client holding at least one voice in a grant map. */
export function grantHolders(grants) {
  const holders = new Set();
  for (const entry of Object.values(grants ?? {})) {
    for (const clientId of Object.keys(entry ?? {})) holders.add(clientId);
  }
  return holders;
}

/**
 * The dimension segment used by a parameter that has none — a play-mode single param, or a gain
 * stage. An address always carries a dimension so the shape never varies, and everything keyed on
 * "which control is this" (the address itself, and the hold on it) agrees about what to say when
 * the answer is "no dimension".
 */
export const NO_DIMENSION = 'main';

/** Address of one axis value on one voice. Values on the wire are normalized 0–1 floats. */
export function paramAddress(wireVoiceId, axis, dimensionId) {
  return `/voice/${wireVoiceId}/param/${axis}/${dimensionId}`;
}

/** Address of a voice's active-dimension switch (bundled with the new dimension's axis values). */
export function dimensionAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/dimension`;
}

/** Address of a voice transport event; `kind` is 'start' | 'stop'. Args carry [atBeat]. */
export function transportAddress(wireVoiceId, kind) {
  return `/voice/${wireVoiceId}/transport/${kind}`;
}

/**
 * Address of a voice's SEEK. Args carry the phase ANCHOR the seek establishes:
 * `[anchorBeat, anchorSourceMs]` — "at shared beat `anchorBeat` this voice is at raw source position
 * `anchorSourceMs`". The anchor is both the command and the durable state, which is why it is sent
 * instead of a target position: every client schedules to that beat and then plays the position the
 * anchor PROJECTS at the moment it fires, so a late delivery, a slow timer, or a tempo change in
 * flight all self-correct. `anchorSourceMs` is RAW — each client adds its own device output lead.
 */
export function seekAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/seek`;
}

/** Address of a voice's loop ENABLE toggle. Args carry [0|1]. */
export function loopEnableAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/loop/enable`;
}

/**
 * Address of a voice's loop RANGE. Args carry [startMs, endMs, active01] to set the loop, or [null]
 * to clear it. The range is already grid-snapped by the sender (loop editing quantizes UI-side), so
 * every client applies the same numbers.
 */
export function loopRangeAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/loop/range`;
}

/**
 * Address of a voice's loop SUBDIVISION selection — which size preset produced the current loop.
 * Args carry [beats], or [null] for "no preset describes this loop" (a freeform edit). The loop
 * RANGE is what makes the two clients sound the same and is already routed; this rides on top so
 * both panels also READ the same, because the size a tap meant cannot be recovered from a range
 * (a hand-drawn band that happens to be four beats long is not the "4" preset).
 */
export function loopSizeAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/loop/size`;
}

/**
 * Address of a voice's NATIVE (audiofile) tempo — `Deck.nativeTempo`, the per-track value the kit
 * panel edits and persists, never the transport tempo. Args carry [bpm].
 *
 * This is shared state because it is the DIVISOR of the shared-phase projection
 * (`anchorSourceMs + (beatNow − anchorBeat) · 60000/nativeTempo`): two clients holding different
 * values separate at a rate proportional to the difference, without limit.
 */
export function nativeTempoAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/native-tempo`;
}

/**
 * Address of a voice's DOWNBEAT — the grid marker in source seconds (`Deck.gridMarkerTimeSec`).
 * Args carry [sec]. Shared for the same reason as the native tempo: it is the ORIGIN of the
 * un-anchored projection, so two clients holding different values sit a constant offset apart from
 * the very first note.
 */
export function downbeatAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/downbeat`;
}

/**
 * Address of a voice's LAUNCH GRID in bars (0 = none). Args carry [bars]. The grid decides which
 * beat a start, a routed seek and a drift correction quantize to, so two clients on different grids
 * pick different boundaries for the same shared event and land on different bars.
 */
export function launchGridAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/launch-grid`;
}

/**
 * Address of a voice's WARP toggle (time-stretch to the transport tempo). Args carry [0|1]. Applied
 * immediately on receipt — warp is a rate re-fan, not a scheduled transport event, so it takes the
 * same no-quantum path a loop enable does.
 */
export function warpAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/warp`;
}

/**
 * Address of a voice's SYNC toggle (does this voice ride the shared pulse). Args carry [0|1]. Like
 * warp it applies immediately on receipt — it changes which clock the voice follows, not when a
 * transport event happens, so it needs no quantum. Stepping off the pulse is a musical choice, and
 * every client must render that voice the same way or the room disagrees about who is locked.
 */
export function syncAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/sync`;
}

/**
 * Address of one Cosmic LFO CONTROL on one axis of a voice. `field` is:
 *   'enable'   — args [0|1], the hexagon;
 *   'source'   — args [sourceKey], which cosmic body drives the frequency;
 *   'waveform' — args [waveformKey];
 *   'kick'     — args ['half'|'double'], a momentary ×0.5 / ×2 frequency step;
 *   'rate'     — args [rateKey], the musical rate a tempo-locked LFO runs at.
 *
 * The rate rides its own field rather than being left to the frequency it produces: the Hz is
 * derived from the deck's tempo, so a peer on the same tempo would compute the same number — but
 * the CHOICE is not recoverable from it, and the two panels would read differently. The same reason
 * the loop SIZE is routed on top of the loop range.
 *
 * Every one of them is a discrete ACT — a button or a select. Press it and both sides change;
 * either player may press it again. So they take no lease and no lock: a lease is a hand on a
 * knob, and nobody's hand rests on a button. The LFO's two continuous knobs (depth, manual
 * frequency) are ordinary routed params and are NOT here.
 *
 * No dimension segment: the active dimension is itself routed, so both ends are on the same one
 * when the act lands, and the snapshot carries the settled state for whichever it is.
 */
export function cosmicAddress(wireVoiceId, axis, field) {
  return `/voice/${wireVoiceId}/cosmic/${axis}/${field}`;
}

/**
 * Address of the device-SENSOR enable on one axis of a voice. Args carry [0|1].
 *
 * One shared button: two people at one instrument with one control panel between them. It reads the
 * same on both sides, and either player may switch it off. Whoever switches it ON drives the axis
 * with their OWN device — so the enable settles two things at once, and it settles them with the
 * same last-write-wins rule: a peer's ON means the presser is not you, and your own device stands
 * down. Nothing else is needed for a handover (press off, press on) or for the case where both
 * press at the same instant.
 *
 * Discrete, so no lease and no lock — a lease is a hand on a knob, and nobody's hand rests on a
 * button. The VALUES the sensor then pushes are ordinary routed axis params, and already were: a
 * generator driving a control is not a hand on it.
 *
 * No dimension segment, for the same reason the Cosmic LFO's controls carry none: the active
 * dimension is itself routed, and the snapshot carries the settled state for whichever it is.
 */
export function sensorAddress(wireVoiceId, axis) {
  return `/voice/${wireVoiceId}/sensor/${axis}/enable`;
}

/** Address of a voice's interaction-PANEL selection mirror. Args carry [panelId]. */
export function panelAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/panel`;
}

/**
 * Address of a voice's CAMERA-focus mirror — what the deck's camera orbits ('world' | 'moon').
 * Args carry [focus]. A view mirror like the panel selection: the slot holder's choice of WHAT to
 * look at mirrors on that deck for everyone; the free orbit/zoom (each viewer's own perspective
 * drag) stays local by the whitelist.
 */
export function cameraAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/camera`;
}

/**
 * Address of a voice's GAIN-RAIL monitoring selection — which stage the rail is pointed at
 * ('input' | 'output'). Args carry [channel]. A view mirror like the panel selection: the switch only
 * changes what is being LOOKED at (both gains stay bound and routed either way), but a deck everyone
 * is watching should show everyone the same stage, or two players read different numbers off what
 * looks like the same fader.
 */
export function gainChannelAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/gain-channel`;
}

/** Address of a voice's INFO-panel open/close mirror. Args carry [mode] (`null` = closed). */
export function infoPanelAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/info-panel`;
}

/**
 * Address of a structural DECK load — a player deals a pool card onto the deck they own. Args carry
 * a single minimal entry reference whose id field names the card's entity kind: `[{ trackId,
 * version }]` (a full deck deal), `[{ orbiterId, version }]` (swap the deck's instrument), or
 * `[{ entangledWorldId, version }]` (swap the deck's world) — every client re-runs the same manual
 * card-load/swap path from it. Owner-gated by the relay exactly like every other `/voice/<id>/`
 * write. Track and orbiter deals carry a `loadId` (two-phase readiness barrier — they rebuild the
 * audio engine); a world deal carries none (visuals only, the engine keeps playing, best-effort).
 * Args `[null]` is a deck CLEAR ("load nothing", the same null-clear convention the loop range
 * uses): the deck empties on every client, no barrier — an empty deck has nothing to arm.
 */
export function loadAddress(wireVoiceId) {
  return `/voice/${wireVoiceId}/load`;
}

/**
 * Parse an inbound address into its routed meaning.
 * @returns {{ kind: 'param', wireVoiceId: string, axis: string, dimensionId: string }
 *         | { kind: 'dimension', wireVoiceId: string }
 *         | { kind: 'transport-start'|'transport-stop', wireVoiceId: string }
 *         | { kind: 'loop-enable'|'loop-range'|'loop-size'|'warp'|'sync'|'seek', wireVoiceId: string }
 *         | { kind: 'native-tempo'|'downbeat'|'launch-grid', wireVoiceId: string }
 *         | { kind: 'panel'|'info-panel'|'camera'|'gain-channel'|'load', wireVoiceId: string }
 *         | { kind: 'cosmic', wireVoiceId: string, axis: string, field: string }
 *         | { kind: 'sensor', wireVoiceId: string, axis: string }
 *         | { kind: 'session-tempo' }
 *         | null} null for anything unrecognized (forward-compatible: unknown addresses are ignored).
 */
export function parseAddress(address) {
  if (typeof address !== 'string') return null;
  if (address === '/session/tempo') return { kind: 'session-tempo' };
  const parts = address.split('/');
  // ['', 'voice', id, …]
  if (parts.length < 4 || parts[0] !== '' || parts[1] !== 'voice') return null;
  const wireVoiceId = parts[2];
  if (!wireVoiceId) return null;
  if (parts[3] === 'param' && parts.length === 6) {
    return { kind: 'param', wireVoiceId, axis: parts[4], dimensionId: parts[5] };
  }
  if (parts[3] === 'dimension' && parts.length === 4) {
    return { kind: 'dimension', wireVoiceId };
  }
  if (parts[3] === 'transport' && parts.length === 5 && (parts[4] === 'start' || parts[4] === 'stop')) {
    return { kind: `transport-${parts[4]}`, wireVoiceId };
  }
  if (parts[3] === 'loop' && parts.length === 5
    && (parts[4] === 'enable' || parts[4] === 'range' || parts[4] === 'size')) {
    return { kind: `loop-${parts[4]}`, wireVoiceId };
  }
  if (parts[3] === 'native-tempo' && parts.length === 4) {
    return { kind: 'native-tempo', wireVoiceId };
  }
  if (parts[3] === 'downbeat' && parts.length === 4) {
    return { kind: 'downbeat', wireVoiceId };
  }
  if (parts[3] === 'launch-grid' && parts.length === 4) {
    return { kind: 'launch-grid', wireVoiceId };
  }
  if (parts[3] === 'seek' && parts.length === 4) {
    return { kind: 'seek', wireVoiceId };
  }
  if (parts[3] === 'warp' && parts.length === 4) {
    return { kind: 'warp', wireVoiceId };
  }
  if (parts[3] === 'sync' && parts.length === 4) {
    return { kind: 'sync', wireVoiceId };
  }
  if (parts[3] === 'panel' && parts.length === 4) {
    return { kind: 'panel', wireVoiceId };
  }
  if (parts[3] === 'info-panel' && parts.length === 4) {
    return { kind: 'info-panel', wireVoiceId };
  }
  if (parts[3] === 'camera' && parts.length === 4) {
    return { kind: 'camera', wireVoiceId };
  }
  if (parts[3] === 'gain-channel' && parts.length === 4) {
    return { kind: 'gain-channel', wireVoiceId };
  }
  if (parts[3] === 'load' && parts.length === 4) {
    return { kind: 'load', wireVoiceId };
  }
  if (parts[3] === 'cosmic' && parts.length === 6) {
    return { kind: 'cosmic', wireVoiceId, axis: parts[4], field: parts[5] };
  }
  if (parts[3] === 'sensor' && parts.length === 6 && parts[5] === 'enable') {
    return { kind: 'sensor', wireVoiceId, axis: parts[4] };
  }
  return null;
}

/** A short random message id (chat dedupe / send-refusal correlation). */
export function randomMsgId() {
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Build the envelope stamper: adds `t` (wall clock), `st` (session-relative ms, `t − sessionEpoch`)
 * and a per-sender monotonic `seq` to every outgoing player event. One stamper per connection —
 * receivers detect a gap in OUR seq and ask for a state refresh, so every player event (param,
 * bundle, transport, state) must share the one counter.
 * @param {{ now?: () => number, getSessionEpoch: () => (number|null) }} deps
 */
export function createEnvelopeStamper({ now = () => Date.now(), getSessionEpoch }) {
  let seq = 0;
  return function stamp(fields) {
    const t = now();
    const epoch = getSessionEpoch?.();
    seq += 1;
    return {
      ...fields,
      t,
      st: Number.isFinite(epoch) ? t - epoch : null,
      seq,
    };
  };
}

/**
 * Per-sender sequence tracking, aware that a sender can start over.
 *
 * `track(senderId, streamId, seq)` returns:
 *   'ok'    — in order (first message, a new stream, or last+1)
 *   'gap'   — one or more messages were missed (caller should request a state refresh;
 *             the message itself is still current and should be applied)
 *   'stale' — at or before the last seen seq (out-of-order duplicate; caller should drop)
 * Non-finite seqs are treated as 'ok' without advancing (a message with no seq can't gap).
 *
 * `streamId` counts how many connections a sender's SEAT has had. It matters because a client
 * numbers its own messages from one on every connection: without it, a returning member's whole
 * restarted stream reads as duplicates already seen, and every write they make is discarded — on
 * the peers that were connected through the rebind, and not on the ones that were not. Only the
 * newest stream per sender is remembered; relay order guarantees no older one can follow it.
 */
export function createSeqTracker() {
  const last = new Map();
  return {
    track(senderId, streamId, seq) {
      if (!Number.isFinite(seq)) return 'ok';
      const prev = last.get(senderId);
      if (prev === undefined || prev.streamId !== streamId) {
        last.set(senderId, { streamId, seq });
        return 'ok';
      }
      if (seq <= prev.seq) return 'stale';
      const gapped = seq !== prev.seq + 1;
      prev.seq = seq;
      return gapped ? 'gap' : 'ok';
    },
    /** Forget a sender (they left; a return starts a fresh stream anyway). */
    forget(senderId) {
      last.delete(senderId);
    },
  };
}

/**
 * Beats until the next bar boundary on a shared beat grid, expressed as the ABSOLUTE target beat.
 * Mirrors the bar-delay rule the quantized start uses: land on the next multiple of `beatsPerBar`,
 * and when that boundary is closer than `slackMs` (too tight to schedule reliably) push a full bar.
 * @returns {number|null} the target beat, or null when the inputs can't produce one.
 */
export function nextBarBeat({ beatNow, beatsPerBar, bpm, slackMs = 40 }) {
  if (!Number.isFinite(beatNow) || !(beatsPerBar > 0) || !Number.isFinite(bpm) || bpm <= 0) return null;
  const EPS = 1e-6;
  const msPerBeat = 60_000 / bpm;
  let target = Math.ceil((beatNow + EPS) / beatsPerBar) * beatsPerBar;
  if ((target - beatNow) * msPerBeat < slackMs) target += beatsPerBar;
  return target;
}

/** The next whole beat (stop quantum). Same slack rule, one beat instead of a bar. */
export function nextBeat({ beatNow, bpm, slackMs = 40 }) {
  return nextBarBeat({ beatNow, beatsPerBar: 1, bpm, slackMs });
}

/** Wall-clock ms from `beatNow` to an absolute target beat (≤0 when the beat already passed). */
export function delayMsToBeat({ beatNow, targetBeat, bpm }) {
  if (!Number.isFinite(beatNow) || !Number.isFinite(targetBeat) || !Number.isFinite(bpm) || bpm <= 0) {
    return null;
  }
  return (targetBeat - beatNow) * (60_000 / bpm);
}
